import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  MetricCounter,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import {
  HOOK,
  SYMPTOM,
  DIAGNOSIS,
  FIX1,
  PLATEAU,
  FIX2,
  COMPOSE,
  PAYOFF,
  OUTRO,
} from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1560,
    }}
  >
    {children}
  </div>
);

const mono = (size = 22): React.CSSProperties => ({
  fontFamily: FONTS.mono,
  fontSize: size,
  lineHeight: 1.85,
  color: COLORS.text,
});

/* 01 — HOOK: fabricated code flow */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const stamp = interpolate(frame, [44, 70], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="code rag · what broke" color={COLORS.cyan} />
          <div style={{ ...mono(28), color: COLORS.dim, textAlign: "left" }}>
            batchAnalysisService.ts
            <br />
            &nbsp;&nbsp;→ analysisEngine.ts
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;→ resultAggregator.ts
          </div>
          <div
            style={{
              marginTop: 30,
              fontFamily: FONTS.display,
              fontWeight: 700,
              fontSize: 64,
              letterSpacing: "-0.02em",
              color: COLORS.red,
              opacity: 0.3 + stamp * 0.7,
            }}
          >
            none of these files exist
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 02 — SYMPTOM: 75% fabricated an architecture */
export const Scene02Symptom: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.symptom.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="the silent failure · ~75%" color={COLORS.red} />
        <div style={{ display: "flex", gap: 28, alignItems: "stretch" }}>
          <Panel accent="red" title="what the llm returned" width={520}>
            <div style={mono(20)}>
              <div>service → engine → aggregator</div>
              <div style={{ color: COLORS.dim }}>clean separation of concerns…</div>
              <div style={{ color: COLORS.red, marginTop: 8 }}>…0 real files. Invented.</div>
            </div>
          </Panel>
          <Panel accent="cyan" title="why it fabricated" width={520}>
            <div style={mono(20)}>
              <div>context window: docs chunks only</div>
              <div>implementation code: <span style={{ color: COLORS.red }}>0 chunks</span></div>
              <div style={{ color: COLORS.dim }}>retrieval never surfaced the code</div>
            </div>
          </Panel>
        </div>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 19,
            color: COLORS.amber,
            marginTop: 26,
            letterSpacing: "0.04em",
          }}
        >
          11 pipeline stages → all green · normal latency · no errors
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={SYMPTOM} accent="red" />
  </AbsoluteFill>
);

/* 03 — DIAGNOSIS: BM25 returned 0 rows for weeks */
export const Scene03Diagnosis: React.FC = () => {
  const frame = useCurrentFrame();
  const zero = interpolate(frame, [30, 58], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.diagnosis.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="check the plumbing" color={COLORS.amber} />
          <div style={{ display: "flex", gap: 30, alignItems: "center" }}>
            <Panel accent="amber" title="raw bm25 query · by hand" width={560}>
              <div style={mono(19)}>
                <div style={{ color: COLORS.dim }}>plainto_tsquery('simple',</div>
                <div>&nbsp;'how does batch analysis work')</div>
                <div style={{ color: COLORS.amber, marginTop: 6 }}>
                  = 'how' &amp; 'does' &amp; 'batch' &amp; 'analysis' &amp; …
                </div>
              </div>
            </Panel>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontFamily: FONTS.display,
                  fontWeight: 700,
                  fontSize: 130,
                  lineHeight: 1,
                  color: COLORS.red,
                  opacity: 0.35 + zero * 0.65,
                }}
              >
                0
              </div>
              <div style={{ fontFamily: FONTS.mono, fontSize: 18, color: COLORS.dim }}>
                rows · for weeks
              </div>
            </div>
          </div>
          <div
            style={{
              fontFamily: FONTS.mono,
              fontSize: 19,
              color: COLORS.dim,
              marginTop: 24,
            }}
          >
            AND-semantics: every word must match one chunk · none do
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={DIAGNOSIS} accent="amber" />
    </AbsoluteFill>
  );
};

/* 04 — FIX 1: migration 033 OR-tsquery */
const OR_SQL = `-- 033_fix_hybrid_search_or_semantics.sql
create function text_to_or_tsquery(query_text text)
returns tsquery as $$
  -- 1. split + lowercase, drop ~100 stop-words & len < 3
  -- 2. cap at 12 terms (perf)
  -- 3. OR-join, not AND
  return to_tsquery('simple',
    array_to_string(filtered, ' | '));
$$;
-- chunks matching MORE terms rank higher (ts_rank_cd),
-- chunks matching 1-2 key terms still surface.`;
export const Scene04Fix1: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.fix1.duration} />
    <AbsoluteFill style={{ ...center, paddingBottom: 40 }}>
      <Kicker text="fix 1 of 10 · biggest win" color={COLORS.green} />
      <div style={{ display: "flex", gap: 30, alignItems: "center" }}>
        <CodePanel
          code={OR_SQL}
          language="sql"
          filename="033_fix_hybrid_search_or_semantics.sql"
          highlightLines={[8, 9]}
          revealFrames={230}
          fontSize={20}
          width={920}
        />
        <Panel accent="green" title="result" width={300}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 16, color: COLORS.dim }}>
              bm25 results
            </div>
            <div
              style={{
                fontFamily: FONTS.display,
                fontWeight: 700,
                fontSize: 52,
                color: COLORS.text,
                margin: "6px 0 14px",
              }}
            >
              <span style={{ color: COLORS.red }}>0</span> → <span style={{ color: COLORS.green }}>15–20</span>
            </div>
            <div style={{ fontFamily: FONTS.mono, fontSize: 17, color: COLORS.lime }}>
              ~75% → 85%
            </div>
          </div>
        </Panel>
      </div>
    </AbsoluteFill>
    <Caption lines={FIX1} accent="green" />
  </AbsoluteFill>
);

/* 05 — PLATEAU: stuck at 85% */
export const Scene05Plateau: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.plateau.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="the 85% ceiling" color={COLORS.amber} />
        <Big size={72}>
          BM25 was on, but{" "}
          <span style={{ color: COLORS.amber }}>handleBatchAnalysis</span>
          <br />
          still ranked <span style={{ color: COLORS.red }}>#11</span>.
        </Big>
        <Panel accent="amber" title="to_tsvector('simple', …)" width={760} style={{ marginTop: 34 }}>
          <div style={{ ...mono(22), textAlign: "center" }}>
            'handleBatchAnalysis' →{" "}
            <span style={{ color: COLORS.amber }}>'handlebatchanalysis':1</span>
            <div style={{ fontSize: 18, color: COLORS.dim, marginTop: 6 }}>
              one token · 'batch' can't match it
            </div>
          </div>
        </Panel>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={PLATEAU} accent="amber" />
  </AbsoluteFill>
);

/* 06 — FIX 2: migration 034 camelCase split + weighted tsvector */
const SPLIT_SQL = `-- 034_camelcase_split_weighted_tsvector.sql
create function split_identifiers(input text) returns text as $$
  -- camelCase  →  'camel Case'   (\\1 \\2 on [a-z][A-Z])
  -- snake_case / paths  →  spaces
  return lower(regexp_replace(/* … */ input, '([a-z])([A-Z])', '\\1 \\2', 'g'));
$$;

-- weighted tsvector: title ≫ rank_text ≫ content
new.search_tsvector :=
  setweight(to_tsvector('simple', split_identifiers(chunk_title)), 'A') ||
  setweight(to_tsvector('simple', rank_text), 'B') ||
  setweight(to_tsvector('simple', content),   'D');`;
export const Scene06Fix2: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.fix2.duration} />
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", paddingBottom: 40 }}>
      <Kicker text="fix 4 of 10 · index-side · second-biggest win" color={COLORS.lime} />
      <CodePanel
        code={SPLIT_SQL}
        language="sql"
        filename="034_camelcase_split_weighted_tsvector.sql"
        highlightLines={[3, 10, 11]}
        revealFrames={320}
        fontSize={21}
        width={1300}
      />
      <div style={{ fontFamily: FONTS.mono, fontSize: 19, color: COLORS.dim, marginTop: 18 }}>
        'handle':A 'batch':A 'analysis':A 'handlebatchanalysis':A &nbsp;·&nbsp; rank{" "}
        <span style={{ color: COLORS.red }}>11&nbsp;(0.56)</span> →{" "}
        <span style={{ color: COLORS.lime }}>4&nbsp;(2.23)</span>
      </div>
    </AbsoluteFill>
    <Caption lines={FIX2} accent="lime" />
  </AbsoluteFill>
);

/* 07 — COMPOSE: 10 fixes, 11 → 16 steps */
const PIPE: PipeNode[] = [
  { label: "bm25", value: "OR-tsquery", sub: "mig 033", accent: "green" },
  { label: "index", value: "camelCase", sub: "mig 034", accent: "lime" },
  { label: "title", value: "multi-term", sub: "step 3.5", accent: "cyan" },
  { label: "graph", value: "JSX-aware", sub: "2-level", accent: "violet" },
  { label: "rerank", value: "hard filter", sub: "+ re-inject", accent: "amber" },
];
export const Scene07Compose: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.compose.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 70px" }}>
      <Kicker text="10 fixes · 2 migrations · 11 → 16 steps" color={COLORS.cyan} />
      <div style={{ width: 1740 }}>
        <PipelineGraph nodes={PIPE} startFrame={6} stepFrames={20} nodeWidth={250} />
      </div>
      <div
        style={{
          fontFamily: FONTS.body,
          fontSize: 28,
          color: COLORS.dim,
          marginTop: 40,
          maxWidth: 1300,
          textAlign: "center",
        }}
      >
        No single fix solved it. Remove any one — accuracy drops.
      </div>
    </AbsoluteFill>
    <Caption lines={COMPOSE} accent="cyan" />
  </AbsoluteFill>
);

/* 08 — PAYOFF: ~75% → peak 90% */
export const Scene08Payoff: React.FC = () => {
  const frame = useCurrentFrame();
  const c1 = interpolate(frame, [120, 150], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const c2 = interpolate(frame, [220, 250], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.payoff.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="code-query accuracy" color={COLORS.lime} />
        <div style={{ display: "flex", alignItems: "baseline", gap: 18 }}>
          <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 60, color: COLORS.dim }}>~75%</span>
          <span style={{ fontFamily: FONTS.display, fontSize: 56, color: COLORS.dim }}>→</span>
          <MetricCounter value={90} from={75} startFrame={10} durationFrames={66} accent="lime" suffix="%" fontSize={150} />
        </div>
        <div style={{ fontFamily: FONTS.mono, fontSize: 20, color: COLORS.dim, marginTop: 10 }}>
          83–90% band · peak 90% · doc queries ~90% unchanged
        </div>
        <div style={{ display: "flex", gap: 30, marginTop: 36 }}>
          <div style={{ opacity: c1 }}>
            <Callout label="mig 033 · OR-tsquery" accent="green" width={360}>
              dead BM25 → <b style={{ color: COLORS.text }}>15–20 results</b>, +10 pts
            </Callout>
          </div>
          <div style={{ opacity: c2 }}>
            <Callout label="mig 034 · camelCase split" accent="lime" width={360}>
              broke the <b style={{ color: COLORS.text }}>85% ceiling</b> → peak 90%
            </Callout>
          </div>
        </div>
      </AbsoluteFill>
      <Caption lines={PAYOFF} accent="lime" />
    </AbsoluteFill>
  );
};

/* 09 — OUTRO */
export const Scene09Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={70}>
          Check the plumbing
          <br />
          before adding complexity.
          <br />
          <span style={{ color: COLORS.cyan }}>BM25 was dead for weeks.</span>
        </Big>
        <div style={{ fontFamily: FONTS.mono, fontSize: 24, letterSpacing: "0.24em", color: COLORS.dim, marginTop: 44 }}>
          EAKC · grounded retrieval
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
